"use client";
import React, { FC, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import Button from "./Button";
import NewsletterSignup from "@/components/form/NewsletterSignup";
import { andrewIdentity } from "@/lib/seo/identity";

const Footer: FC = () => {
  /**
   * Opens the Cookiebot consent dialog to allow users to update their cookie preferences.
   * Uses the Cookiebot.renew() method as recommended by Cookiebot documentation.
   * @see https://www.cookiebot.com/en/developer/
   */
  const handleCookieSettings = useCallback(() => {
    if (typeof window !== "undefined" && window.Cookiebot) {
      window.Cookiebot.renew();
    }
  }, []);

  return (
    <footer className="w-full bg-slate-950">
      {/* Newsletter Section */}
      <div className="mx-auto max-w-7xl px-6 md:px-8 pt-24 pb-8 border-b border-slate-800">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-24">
          <div className="flex-1 text-center lg:text-left">
            <h3 className="font-semibold text-slate-200 text-lg mb-2">
              Get weekly Agile insights
            </h3>
            <p className="text-slate-400 text-sm">
            A weekly newsletter for engineering managers, delivery leads, and technical founders who want to improve how their teams build and ship software.
            </p>
          </div>
          <div className="flex-1 w-full lg:w-auto">
            <NewsletterSignup variant="inline" source="footer" />
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-6 md:px-8 pt-12 pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10 text-sm">
        {/* Column 1: About & Social */}
        <div className="flex flex-col">
          <Link
            href="/"
            className="mb-4 inline-block w-fit hover:opacity-80 transition-opacity"
            aria-label="Go to homepage"
          >
            <Image
              src="/images/aleger-white-logo.svg"
              alt="Andrew Leger reverse logo"
              width={72}
              height={35}
            />
          </Link>
          <p className="mb-4 text-slate-400">
            Supporting engineering teams and companies by improving software delivery, building strong Agile systems, and guiding development and product execution.
          </p>
          <p className="mb-6 text-xs text-slate-400 font-medium">
            15+ years of experience
          </p>
          <div className="flex flex-row gap-4">
            <Link
              href={andrewIdentity.linkedin}
              rel="noopener noreferrer"
              target="_blank"
              className="hover:opacity-80 transition-opacity"
              aria-label="LinkedIn"
            >
              <Image
                src="/images/linkedin.svg"
                alt="LinkedIn logo"
                width={35}
                height={35}
              />
            </Link>
            <Link
              href={andrewIdentity.github}
              rel="noopener noreferrer"
              target="_blank"
              className="hover:opacity-80 transition-opacity"
              aria-label="GitHub"
            >
              <Image
                src="/images/github.svg"
                alt="GitHub logo"
                width={35}
                height={35}
              />
            </Link>
            <Link
              href={andrewIdentity.twitter}
              rel="noopener noreferrer"
              target="_blank"
              className="hover:opacity-80 transition-opacity"
              aria-label="Twitter X"
            >
              <Image
                src="/images/x.svg"
                alt="Twitter X logo"
                width={35}
                height={35}
              />
            </Link>
          </div>
        </div>

        {/* Column 2: Consulting Services */}
        <div className="flex flex-col">
          <p className="font-bold text-slate-200 mb-4 text-base">Consulting Services</p>
          <ul className="space-y-3">
            <li>
              <Link
                href="/consulting"
                className="text-slate-400 hover:text-slate-200 transition-colors block"
              >
                <span className="font-medium">Agile Transformation</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Team structure & workflow optimization
                </span>
                <span className="block text-xs text-slate-400">
                  Process improvement & best practices
                </span>
              </Link>
            </li>
            <li>
              <Link
                href="/consulting"
                className="text-slate-400 hover:text-slate-200 transition-colors block"
              >
                <span className="font-medium">Engineering Management</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Team leadership & development
                </span>
                <span className="block text-xs text-slate-400">
                  Delivery optimization
                </span>
              </Link>
            </li>
            <li>
              <Link
                href="/consulting"
                className="text-slate-400 hover:text-slate-200 transition-colors block"
              >
                <span className="font-medium">Tool Configuration</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Custom tool setup & integration
                </span>
                <span className="block text-xs text-slate-400">
                  Jira, Azure DevOps, ClickUp
                </span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 3: Products */}
        <div className="flex flex-col">
          <p className="font-bold text-slate-200 mb-4 text-base">Products</p>
          <ul className="space-y-3">
            <li>
              <Link
                href="https://servbuilder.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-200 transition-colors block"
              >
                <span className="font-medium">ServBuilder</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Field service management
                </span>
              </Link>
            </li>
            <li>
              <Link
                href="https://devflowops.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-200 transition-colors block"
              >
                <span className="font-medium">DevFlowOps</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Agile Command Center
                </span>
              </Link>
            </li>
            <li>
              <Link
                href="https://churnrate.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-200 transition-colors block"
              >
                <span className="font-medium">ChurnRate</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  SaaS churn analytics
                </span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 4: Resources & Support */}
        <div className="flex flex-col">
          <p className="font-bold text-slate-200 mb-4 text-base">Resources & Support</p>
          <ul className="space-y-3">
            <li>
              <Link
                href="/"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                href="/resume"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Resume
              </Link>
            </li>
            <li>
              <Link
                href="/contact"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Contact Me
              </Link>
            </li>
            <li>
              <Link
                href="/video-walkthroughs"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Video Walkthroughs
              </Link>
            </li>
            <li>
              <Link
                href="/case-studies"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Case Studies
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 5: Legal & Company */}
        <div className="flex flex-col">
          <p className="font-bold text-slate-200 mb-4 text-base">Legal & Company</p>
          <ul className="space-y-3">
            <li>
              <Link
                href="/terms-of-service"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Terms of Service
              </Link>
            </li>
            <li>
              <Link
                href="/privacy-statement"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Privacy Statement
              </Link>
            </li>
            <li>
              <Link
                href="/cookie-policy"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Cookie Policy
              </Link>
            </li>
            <li>
              <button
                onClick={handleCookieSettings}
                className="text-slate-400 hover:text-slate-200 transition-colors text-left"
                type="button"
              >
                Cookie Settings
              </button>
            </li>
          </ul>
          <div className="mt-6">
            <Link
              href="https://www.dmca.com/"
              rel="noopener noreferrer"
              target="_blank"
              className="inline-block"
            >
              <Image
                src="https://imagedelivery.net/dZMNLiwpNY0_jKeBRDZ4_g/64532f1a-d358-4db8-cb57-464d8fb74900/public"
                alt="DMCA Logo"
                width={100}
                height={20}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="mx-auto max-w-7xl px-6 md:px-8 pb-12 border-t border-slate-800">
        <div className="pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-xs text-slate-400">
              <span>
                © {new Date().getFullYear()}{" "}
                <Link href="/" className="hover:text-slate-200 transition-colors">
                  AJLDev, LLC
                </Link>
                . All Rights Reserved.
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
